import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { Row, Col, Button, ListGroup, Alert } from "react-bootstrap";
import Moment from "moment";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

function VolumeFoliosList({
  data,
  isLoading,
  isAuthenticated = true,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  filter,
  user,
  validateRelatedRecord,
  dataFetch,
  stateCode,
}) {
  const dataSet = "volumeFolios";
  const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const columnHelper = createColumnHelper();
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [copyInProgress, setCopyInProgress] = useState(false);
  
  // Track edited values for new records so they persist across multiple blur events
  const editedValuesRef = useRef({});
  const shouldAutoSortOnOpenRef = useRef(true);

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    handleSave: async ({ row, column, value }) => {
      // Once user starts editing/saving, stop automatic resorting for this modal session.
      shouldAutoSortOnOpenRef.current = false;

      const isNew = row.original.new_record === true || !row.original.volume_folio_id;
      const rowId = row.original.volume_folio_id || `temp_${row.index}`;

      // Build the updated record with the new value overlaid
      let record = {
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
      };

      // For new records, track edited values across multiple blur events
      if (isNew) {
        // Store the new value
        if (!editedValuesRef.current[rowId]) {
          editedValuesRef.current[rowId] = {};
        }
        editedValuesRef.current[rowId][column?.id] = value;

        // Merge with previously edited values
        record = {
          ...row.original,
          ...editedValuesRef.current[rowId],
        };

        const hasVolume = record.volume && String(record.volume).trim();
        const hasFolio = record.folio && String(record.folio).trim();
        const hasOther = record.other && String(record.other).trim();
        
        // Only save if BOTH volume AND folio, OR if other is filled
        const shouldSave = (hasVolume && hasFolio) || hasOther;

        if (!shouldSave) {
          // Not ready to persist yet — EditableCell will handle updateData automatically
          console.log(
            `Skipping save for new record: volume=${record.volume}, folio=${record.folio}, other=${record.other}`
          );
          // Return undefined so EditableCell updates local state
          return;
        }

        // Apply mutual exclusivity logic before saving
        if (hasVolume && hasFolio) {
          record.other = null; // Clear other if both volume and folio are filled
        } else if (hasOther) {
          record.volume = null; // Clear volume/folio if other is filled
          record.folio = null;
        }

        // Clear the tracked values after saving
        delete editedValuesRef.current[rowId];
      }

      try {
        console.log("Saving record:", record);
        const saveResult = await saveRecord(dataSet, record);
        const savedId =
          saveResult?.id || saveResult?.volume_folio_id || record.volume_folio_id;

        // Fetch authoritative row values (e.g. title_reference) but keep row position stable.
        let hydratedData = null;
        if (dataFetch) {
          hydratedData = await dataFetch(true);
        }

        if (savedId != null && Array.isArray(hydratedData) && hydratedData.length > 0) {
          const localSavedRow = {
            ...record,
            ...saveResult,
            volume_folio_id: savedId,
            new_record: false,
            modified: false,
          };

          const serverSavedRow = hydratedData.find(
            (vf) => String(vf?.volume_folio_id) === String(savedId)
          );
          const mergedSavedRow = serverSavedRow
            ? { ...localSavedRow, ...serverSavedRow }
            : localSavedRow;

          const nextRows = hydratedData.filter(
            (vf) => String(vf?.volume_folio_id) !== String(savedId)
          );
          const targetIndex = Math.min(row.index, nextRows.length);
          nextRows.splice(targetIndex, 0, mergedSavedRow);

          updateTableState(nextRows, dataSet);
          console.log("Record saved successfully via parent logic");
          return { skipLocalUpdate: true };
        }

        const localData = [...tableData];
        localData[row.index] = {
          ...localData[row.index],
          ...record,
          ...saveResult,
          volume_folio_id: savedId,
          new_record: false,
          modified: false,
        };

        updateTableState(localData, dataSet);

        console.log("Record saved successfully via parent logic");
        return { skipLocalUpdate: true };
      } catch (err) {
        console.error("Save failed:", err);
        setErrorMessage(err.message || "Save failed");
        setShowAlert(true);
        return { saveFailed: true };
      }
    },
    handleDelete: ({ row }) => {
      deleteRecord(dataSet, row.original);
    },
  };

  const columns = [
    columnHelper.accessor("volume_folio_id", {
      id: "volume_folio_id",
      header: "ID",
      size: 60,
    }),
    columnHelper.accessor("title_reference", {
      id: "title_reference",
      header: "Title Reference",
      cell: (cellContext) => {
        const value = cellContext.getValue();
        return value ?? "";
      },
      size: 200,
      meta: {
        wrap: true,
        cellClassName: "lavender-cell",
      },
    }),
    columnHelper.accessor("volume", {
      id: "volume",
      header: "Volume",
      cell: EditableCell,
      size: 100,
    }),
    columnHelper.accessor("folio", {
      id: "folio",
      header: "Folio",
      cell: EditableCell,
      size: 200,
    }),
    columnHelper.accessor("other", {
      id: "other",
      header: "Other",
      cell: EditableCell,
      size: 200,
    }),
    columnHelper.accessor("created_date", {
      id: "created_date",
      header: "Created Date",
      cell: (cellContext) => {
        const created_date = cellContext.getValue();
        if (created_date === null) {
          return null;
        }
        return Moment(created_date).local().format("DD-MM-YYYY");
      },
      size: 200,
    }),
    columnHelper.accessor("created_by", {
      id: "created_by",
      header: "Created By",
      size: 200,
    }),
    columnHelper.accessor("last_edited_date", {
      id: "last_edited_date",
      header: "Last Edited Date",
      cell: (cellContext) => {
        const last_edited_date = cellContext.getValue();
        if (last_edited_date === null) {
          return null;
        }
        return Moment(last_edited_date).local().format("DD-MM-YYYY");
      },
      size: 200,
    }),
    columnHelper.accessor("last_edited_by", {
      id: "last_edited_by",
      header: "Last Edited By",
      size: 200,
    }),
    columnHelper.display({
      id: "deleteButton",
      header: "",
      cell: (cellContext) => (
        <Button
          type="button"
          size="sm"
          variant="danger"
          onClick={() => {
            const confirmed = window.confirm(
              "Are you sure you want to delete this record?"
            );
            if (!confirmed) {
              return;
            }
            tableMeta.handleDelete({ row: cellContext.row });
          }}
        >
          Delete
        </Button>
      ),
      size: 65,
    }),
    columnHelper.display({
      id: "copyButton",
      header: "",
      cell: (cellContext) => (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={(e) => handleCopyRecord(cellContext.row, e)}
          disabled={copyInProgress}
        >
          Copy
        </Button>
      ),
      size: 65,
    }),
  ];

  const sortData = useCallback(() => {
    // Sorting them all together was too complicated and wasn't working right
    // So sort each 'group' individually, concat, and use their IDs to do the final sort on data
    const vfsWithoutOther = [...tableData]
      .filter((vf) => vf.volume && vf.folio)
      .sort(
        (a, b) =>
          (a.volume || "").localeCompare(b.volume, "en", { numeric: true }) ||
          (a.folio || "").localeCompare(b.folio, "en", { numeric: true })
      );
    const vfsWithOther = [...tableData]
      .filter((vf) => vf.other)
      .sort((a, b) =>
        (a.other || "").localeCompare(b.other, "en", { numeric: true })
      );
    const vfsWithNeither = [...tableData]
      .filter((vf) => !vf.volume && !vf.folio && !vf.other)
      .sort((a, b) => a.volume_folio_id - b.volume_folio_id);
    const vfIds = [...vfsWithoutOther, ...vfsWithOther, ...vfsWithNeither].map(
      (vf) => vf.volume_folio_id
    );

    const sortedData = [...tableData].sort(
      (a, b) => vfIds.indexOf(a.volume_folio_id) - vfIds.indexOf(b.volume_folio_id)
    );

    const hasSameOrder =
      sortedData.length === tableData.length &&
      tableData.every((row, idx) => row === sortedData[idx]);

    if (hasSameOrder) {
      return;
    }

    updateTableState(sortedData, dataSet);
  }, [tableData, updateTableState]);

  useEffect(() => {
    if (!isAuthenticated || controlType !== "table") {
      return;
    }

    if (isLoading || tableData.length === 0 || !shouldAutoSortOnOpenRef.current) {
      return;
    }

    // Keep applying open-session auto-sort so async modal fetch replacements are also sorted.
    // sortData has a no-op guard and won't loop when order is already correct.
    sortData();
  }, [isAuthenticated, controlType, isLoading, tableData, sortData]);

  async function addRecord(event) {
    shouldAutoSortOnOpenRef.current = false;

    let localData = [...tableData];
    let foreign_key = { key: "property_id", value: filter?.propertyid };

    localData.unshift({
      [foreign_key.key]: foreign_key.value,
      title_reference: null,
      volume: null,
      folio: null,
      other: null,
      created_by: user,
      last_edited_date: null,
      last_edited_by: user,
      archived_date: null,
      new_record: true,
    });

    updateTableState(localData, dataSet);
  }

  async function handleCopyRecord(row, event) {
    event.preventDefault();

    shouldAutoSortOnOpenRef.current = false;

    let promptMessage = `How many copies do you want to create?`;
    const numCopies = parseInt(window.prompt(promptMessage));
    if (!numCopies) {
      return;
    }

    let localData = [...tableData];
    let copies = [];

    for (let i = 0; i < numCopies; i++) {
      let copy = { ...row.original };

      copy.volume_folio_id = null;
      copy.modified = true;
      copy.new_record = true;

      // Non-ACT behaviour: increment Folio
      if (parseInt(copy.folio)) {
        if (parseInt(copy.folio) + i + 1 <= 999) {
          copy.folio = (parseInt(copy.folio) + i + 1).toString();
        }
      }
      copies.push(copy);
    }

    // Insert copies immediately after the original
    const copiedVolumeFoliosIdx = localData.findIndex(
      (vf) => vf.volume_folio_id === row.original.volume_folio_id
    );
    localData.splice(copiedVolumeFoliosIdx + 1, 0, ...copies);

    updateTableState(localData, dataSet);

    try {
      setShowAlert(false);
      setErrorMessage("");
      setCopyInProgress(true);

      // UAT parity: immediately persist copied records when user confirms copy count.
      for (const copy of copies) {
        await saveRecord(dataSet, copy);
      }

      if (dataFetch) {
        await dataFetch();
      }
    } catch (error) {
      console.error("Copy volume folios failed:", error);
      setErrorMessage(error?.message || "Failed to copy volume folios");
      setShowAlert(true);
    } finally {
      setCopyInProgress(false);
    }
  }

  function renderList(data, listAmount) {
    if (!data || data.length === 0) {
      return null;
    }

    const sortedData = [...data];
    const vfsWithoutOther = sortedData
      .filter((vf) => vf.volume && vf.folio)
      .sort(
        (a, b) =>
          (a.volume || "").localeCompare(b.volume, "en", { numeric: true }) ||
          (a.folio || "").localeCompare(b.folio, "en", { numeric: true })
      );
    const vfsWithOther = sortedData
      .filter((vf) => vf.other)
      .sort((a, b) =>
        (a.other || "").localeCompare(b.other, "en", { numeric: true })
      );
    const vfsWithNeither = sortedData
      .filter((vf) => !vf.volume && !vf.folio && !vf.other)
      .sort((a, b) => a.volume_folio_id - b.volume_folio_id);
    const vfIds = [...vfsWithoutOther, ...vfsWithOther, ...vfsWithNeither].map(
      (vf) => vf.volume_folio_id
    );
    const listSortedData = [...sortedData].sort(
      (a, b) => vfIds.indexOf(a.volume_folio_id) - vfIds.indexOf(b.volume_folio_id)
    );

    let amt = 0;

    if (listAmount) {
      amt = listSortedData.slice(0, parseInt(listAmount));
    } else {
      amt = listSortedData.slice(0, 5);
    }

    const latest = amt;
    const volumeFolioStr = (vf) => {
      if (vf.volume && !vf.folio) {
        return <span>V{vf.volume}</span>;
      } else if (!vf.volume && vf.folio) {
        return <span>F{vf.folio}</span>;
      } else if (vf.volume && vf.folio) {
        return <span>V{vf.volume} F{vf.folio}</span>;
      } else {
        return <span>{vf.other}</span>;
      }
    };

    return [{}]
      .concat(latest)
      .map((d, i) =>
        i !== 0 ? (
          <ListGroup.Item key={i} as="a" header={d.title_reference}>
            {volumeFolioStr(d)}
          </ListGroup.Item>
        ) : (
          ""
        )
      );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="VolumeFoliosSection">
      {showAlert && <Alert variant="danger">{errorMessage}</Alert>}

      {controlType === "table" && (
        <Row className="show-Container">
          <Col className="text-right">
            <Button variant="secondary" size="sm" onClick={sortData}>
              Refresh Sort
            </Button>
            &nbsp;
            <Button variant="primary" size="sm" onClick={addRecord}>
              Add
            </Button>
          </Col>
        </Row>
      )}

      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : controlType === "table" ? (
            <GenericTable
              data={tableData}
              columns={columns}
              meta={tableMeta}
              pageSize={10}
            />
          ) : (
            renderList(tableData, 5)
          )}
        </Col>
      </Row>
    </div>
  );
}

export default VolumeFoliosList;

